/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { buildAppMetadataLookup } from '@/lib/activityOperations/activityOperationMetadata.js';
import { toDisplayUserLabel } from '@/lib/activityOperations/activityOperationFormatters.js';
import normalizeText from '@/lib/normalizeText.js';
import { buildGovernanceTimelineBuckets, findGovernanceAppName, findGovernanceEntityTypeLabel, readGovernanceRows, resolveMatchingGovernanceResponse } from '@/redux/selectors/pieveSelectors/lib/governanceSelectorHelpers.js';
import { appsMetadataItems } from '@/redux/selectors/pieveSelectors/runtimeSelectors.js';
import { EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';

const dashboardResponse = (state) => state?.pieve?.operationsDashboardResponse || null;
const feedResponse = (state) => state?.pieve?.operationsFeedResponse || null;
const searchResponse = (state) => state?.pieve?.operationsSearchResponse || null;
const requestKeyArg = (_, requestKey = '') => String(requestKey || '');
const feedRequestKeyArg = (_, args = {}) => String(args?.feedRequestKey || '');
const searchRequestKeyArg = (_, args = {}) => String(args?.searchRequestKey || '');
const searchQueryArg = (_, args = {}) => normalizeText(args?.searchQuery);
const appIdArg = (_, args = {}) => normalizeText(args?.appId);
const entityTypeArg = (_, args = {}) => normalizeText(args?.entityType);
const limitArg = (_, args = {}) => {
  const value = Number(args?.limit);

  if (!Number.isFinite(value) || value < 1) {
    return 0;
  }

  return Math.floor(value);
};

const selectFeedSnapshotFromArgs = createSelector([feedResponse, feedRequestKeyArg], (response, requestKey) => {
  return resolveMatchingGovernanceResponse(response, requestKey);
});

const selectSearchSnapshotFromArgs = createSelector([searchResponse, searchRequestKeyArg], (response, requestKey) => {
  return resolveMatchingGovernanceResponse(response, requestKey);
});

export const governanceDashboardSnapshot = createSelector([dashboardResponse, requestKeyArg], (response, requestKey) => {
  return resolveMatchingGovernanceResponse(response, requestKey);
});

export const governanceFeedSnapshot = createSelector([feedResponse, requestKeyArg], (response, requestKey) => {
  return resolveMatchingGovernanceResponse(response, requestKey);
});

export const governanceSearchSnapshot = createSelector([searchResponse, requestKeyArg], (response, requestKey) => {
  return resolveMatchingGovernanceResponse(response, requestKey);
});

export const governanceDashboardError = createSelector([governanceDashboardSnapshot], (response) => {
  if (!response || response.success !== false) {
    return '';
  }

  return String(response?.message || '');
});

export const governanceFeedError = createSelector([governanceFeedSnapshot], (response) => {
  if (!response || response.success !== false) {
    return '';
  }

  return String(response?.message || '');
});

export const governanceSearchError = createSelector([governanceSearchSnapshot], (response) => {
  if (!response || response.success !== false) {
    return '';
  }

  return String(response?.message || '');
});

export const governanceDashboardViewerScope = createSelector([governanceDashboardSnapshot], (response) => {
  return normalizeText(response?.meta?.viewerScope);
});

export const governanceFeedViewerScope = createSelector([governanceFeedSnapshot], (response) => {
  return normalizeText(response?.meta?.viewerScope);
});

export const governanceSearchViewerScope = createSelector([governanceSearchSnapshot], (response) => {
  return normalizeText(response?.meta?.viewerScope);
});

export const governanceDashboardTimelineBuckets = createSelector([governanceDashboardSnapshot], (response) => {
  if (!response || response.success !== true) {
    return EMPTY_ARRAY;
  }

  return buildGovernanceTimelineBuckets(response?.data?.timeline);
});

export const governanceOverviewAppActivityItems = createSelector([governanceDashboardSnapshot], (response) => {
  if (!response || response.success !== true || !Array.isArray(response?.data?.activityByApp)) {
    return EMPTY_ARRAY;
  }

  return response.data.activityByApp
    .map((item) => {
      const appId = normalizeText(item?.appId || item?.appSlug || item?.appName);

      if (!appId) {
        return null;
      }

      return {
        appKey: appId,
        appId: normalizeText(item?.appId),
        appSlug: normalizeText(item?.appSlug),
        appName: normalizeText(item?.appName) || appId,
        create: Number(item?.create || 0),
        update: Number(item?.update || 0),
        delete: Number(item?.delete || 0),
        total: Number(item?.all || 0)
      };
    })
    .filter(Boolean);
});

export const governanceOverviewActiveUsers = createSelector([governanceDashboardSnapshot], (response) => {
  if (!response || response.success !== true || !Array.isArray(response?.data?.activeUsers)) {
    return EMPTY_ARRAY;
  }

  return response.data.activeUsers
    .map((item) => {
      const userId = normalizeText(item?.userId);

      if (!userId) {
        return null;
      }

      return {
        userId,
        label: normalizeText(item?.userDisplay) || toDisplayUserLabel(userId),
        total: Number(item?.all || 0)
      };
    })
    .filter(Boolean);
});

export const governanceAppEntityTypeActivityItems = createSelector([governanceDashboardSnapshot], (response) => {
  if (!response || response.success !== true || !Array.isArray(response?.data?.activityByEntityType)) {
    return EMPTY_ARRAY;
  }

  return response.data.activityByEntityType
    .map((item) => {
      const entityType = normalizeText(item?.entityType);

      if (!entityType) {
        return null;
      }

      return {
        entityType,
        entityTypeLabel: normalizeText(item?.entityTypeLabel) || entityType,
        create: Number(item?.create || 0),
        update: Number(item?.update || 0),
        delete: Number(item?.delete || 0),
        total: Number(item?.all || 0)
      };
    })
    .filter(Boolean);
});

export const governanceFeedRows = createSelector([governanceFeedSnapshot], (response) => {
  return readGovernanceRows(response);
});

export const governanceSearchRows = createSelector([governanceSearchSnapshot], (response) => {
  return readGovernanceRows(response);
});

export const governanceVisibleRows = createSelector([selectFeedSnapshotFromArgs, selectSearchSnapshotFromArgs, searchQueryArg, limitArg], (feedSnapshot, searchSnapshot, searchQuery, limit) => {
  const rows = searchQuery ? readGovernanceRows(searchSnapshot) : readGovernanceRows(feedSnapshot);

  if (!limit) {
    return rows;
  }

  return rows.slice(0, limit);
});

export const governanceAppDisplayName = createSelector([appsMetadataItems, selectFeedSnapshotFromArgs, selectSearchSnapshotFromArgs, appIdArg], (appsMetadata, feedSnapshot, searchSnapshot, appId) => {
  return findGovernanceAppName({
    appMetadataLookup: buildAppMetadataLookup(appsMetadata),
    appId,
    feedRows: readGovernanceRows(feedSnapshot),
    searchRows: readGovernanceRows(searchSnapshot)
  });
});

export const governanceEntityTypeLabel = createSelector([selectFeedSnapshotFromArgs, selectSearchSnapshotFromArgs, entityTypeArg], (feedSnapshot, searchSnapshot, entityType) => {
  return findGovernanceEntityTypeLabel({
    entityType,
    feedRows: readGovernanceRows(feedSnapshot),
    searchRows: readGovernanceRows(searchSnapshot)
  });
});
